/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CommandBarButton } from '@fluentui/react/lib/Button';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getCommandBarButtonStyles } from 'src/core/utils/azureRbacStyles';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { SuggestedUtteranceObservable } from 'src/luis/projects/utterances/utils/generateSuggestedUtterancesObservables';

const messages = defineMessages({
    confirmEntityPredictions: {
        id: 'utterances.suggestUtterancesCommandBar.confirmEntityPredictions',
        defaultMessage: 'Confirm entity predictions'
    },
    confirmEntityPredictionsAll: {
        id: 'utterances.suggestUtterancesCommandBar.confirmEntityPredictionsAll',
        defaultMessage: 'Confirm all entity predictions'
    },
    save: {
        id: 'utterances.suggestUtterancesCommandBar.save',
        defaultMessage: 'Save'
    },
    discard: {
        id: 'utterances.suggestUtterancesCommandBar.discard',
        defaultMessage: 'Discard'
    },
    unconfirmedPredictions: {
        id: 'utterances.suggestUtterancesCommandBar.unconfirmedPredictions',
        defaultMessage: 'Note: You have unconfirmed predictions'
    }
});

export type SuggestUtterancesCommandBarProps = {
    selectedItems: SuggestedUtteranceObservable[];
    isConfirmPredictionsDisabled: boolean;
    hasUnconfirmedPredictions: boolean;
    areActionsDisabled: boolean;
    onSubmit: () => void;
    onDiscard: () => void;
    onConfirmEntityPredictions: () => void;

    /**
     * User role to determine the actions.
     */
    userRole: string;

    /**
     * User role to show in LuisTooltip message.
     */
    displayedUserRole: string;
};

export const SuggestUtterancesCommandBar = (props: SuggestUtterancesCommandBarProps) => {
    const {
        selectedItems,
        isConfirmPredictionsDisabled,
        areActionsDisabled,
        hasUnconfirmedPredictions,
        onConfirmEntityPredictions,
        onDiscard,
        onSubmit,
        userRole,
        displayedUserRole
    } = props;
    const { localize } = useLocalization();
    const noSelectedItems = selectedItems.length === 0;

    const { canWrite } = mapUserRoleToUserActions(userRole);

    const displayedMessage = !noSelectedItems
        ? localize(messages.confirmEntityPredictions)
        : localize(messages.confirmEntityPredictionsAll);

    // onAction functions should be called with no args.
    const commands: ICommandBarItemProps[] = [
        {
            key: 'confirmEntityPredictions',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    displayedUserRole={displayedUserRole}
                    data-automation-id="confirmEntityPredictionsLuisToolTip"
                    hidden={canWrite || item.disabled || areActionsDisabled || isConfirmPredictionsDisabled}
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            role="menuitem"
                            text={displayedMessage}
                            name={displayedMessage}
                            ariaLabel={displayedMessage}
                            iconProps={{ iconName: 'checkmark' }}
                            onClick={() => onConfirmEntityPredictions()}
                            data-automation-id="confirmEntityPredictionsButton"
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            disabled={!canWrite || item.disabled || areActionsDisabled || isConfirmPredictionsDisabled}
                        />
                    }
                />
            )
        },
        {
            key: 'submitUtterance',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    displayedUserRole={displayedUserRole}
                    data-automation-id="submitUtteranceLuisToolTip"
                    hidden={canWrite || item.disabled || areActionsDisabled || noSelectedItems}
                    content={hasUnconfirmedPredictions && !noSelectedItems ? localize(messages.unconfirmedPredictions) : ''}
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            role="menuitem"
                            onClick={() => onSubmit()}
                            text={localize(messages.save)}
                            name={localize(messages.save)}
                            iconProps={{ iconName: 'upload' }}
                            ariaLabel={localize(messages.save)}
                            data-automation-id="submitUtteranceButton"
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            disabled={!canWrite || item.disabled || areActionsDisabled || noSelectedItems}
                        />
                    }
                />
            )
        },
        {
            key: 'discardUtterance',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canWrite || item.disabled || areActionsDisabled || noSelectedItems}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="discardUtteranceLuisToolTip"
                    content={hasUnconfirmedPredictions && !noSelectedItems ? localize(messages.unconfirmedPredictions) : ''}
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            role="menuitem"
                            onClick={() => onDiscard()}
                            text={localize(messages.discard)}
                            name={localize(messages.discard)}
                            iconProps={{ iconName: 'cancel' }}
                            ariaLabel={localize(messages.discard)}
                            data-automation-id="discardUtteranceButton"
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            disabled={!canWrite || item.disabled || areActionsDisabled || noSelectedItems}
                        />
                    }
                />
            )
        }
    ];

    return <LuisCommandBar items={commands} />;
};
